import dayjs from 'dayjs';

// Shared helpers for equipment ordering — used by the manual OrderEquipmentModal
// and by the automatic ordering flow triggered from InstallationDate.

// Returns true when the chosen supplier matches one of the catalog-recommended
// suppliers for the model, or when there is no recommendation to compare against.
// The recommendation is a free-text string that may list several suppliers
// separated by commas / semicolons / slashes / new lines.
export const isSupplierRecommended = (supplierName, recommendedStr) => {
  if (!recommendedStr || !supplierName) return true;
  const name = supplierName.trim().toLowerCase();
  if (!name) return true;
  const tokens = recommendedStr
    .split(/[,;/|\n]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  if (tokens.length === 0) return true;
  return tokens.some((tok) => tok.includes(name) || name.includes(tok));
};

// Extract orderable equipment from a proposal (same semantics as the manual
// order modal): only the accepted option's systems count when the client
// picked one of several options.
export const extractProposalEquipment = (proposal) => {
  const systems = proposal?.proposalSystems || [];
  const activeSystems =
    proposal?.optionAccepted && proposal?.selectedOptionId
      ? systems.filter((s) => s.optionId === proposal.selectedOptionId)
      : systems;

  const equipment = [];
  const seen = new Set();
  activeSystems.forEach((system) => {
    (system.equipments || []).forEach((eq) => {
      if (eq.id && !seen.has(eq.id)) {
        seen.add(eq.id);
        equipment.push({
          id: eq.id,
          type: eq.equipmentType || '-',
          make: eq.manufacturerName || '-',
          model: eq.modelNumber || '-',
          name: eq.equipmentName || '-',
          price: eq.priceAtCreation || 0,
          qty: 1,
          supplierId: null,
          supplierName: '',
          instruction: '',
        });
      }
    });
  });
  return equipment;
};

// Models already covered by orders whose email was actually sent.
export const collectSentModels = (orders) => {
  const sent = new Set();
  (orders || [])
    .filter((o) => o.sentBy)
    .forEach((order) => {
      (order.items || []).forEach((item) => {
        if (item.model && item.model !== '-') sent.add(item.model);
      });
    });
  return sent;
};

// Pick the supplier for a model from the catalog recommendation: the first
// recommended token that matches a known supplier wins. Returns null when
// there is no recommendation or no supplier matches it.
export const resolveSupplierForModel = (model, catalogSuppliers, suppliers) => {
  const rec = catalogSuppliers?.[model];
  if (!rec) return null;
  const tokens = rec
    .split(/[,;/|\n]+/)
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean);
  for (const tok of tokens) {
    const sup = (suppliers || []).find((s) => {
      const n = (s.name || '').trim().toLowerCase();
      return n && (n.includes(tok) || tok.includes(n));
    });
    if (sup) return sup;
  }
  return null;
};

// Parse tonnage from a model number (36 KBTU → 3.0 tons). Zero-padded
// capacity tokens (036, 042...) win; otherwise the LAST bare token is taken,
// because vendors put family/SEER prefixes first (24ABC636 → 36, RD18AY36 →
// 36). Validated against the condenser/HP models in the CRM catalog.
export const parseTonnage = (model) => {
  const s = String(model || '').toUpperCase();
  const padded = [...s.matchAll(/0(18|24|30|36|42|48|60)/g)];
  if (padded.length) return Number(padded[padded.length - 1][1]) / 12;
  const bare = [...s.matchAll(/(18|24|30|36|42|48|60)/g)];
  if (bare.length) return Number(bare[bare.length - 1][1]) / 12;
  return null;
};

// Case-insensitive EXACT type match. Substring matching is dangerous: the
// token "AC" is contained in "furnACe", which made gas-furnace contracts get
// a second disconnect/whip/pad (furnace models also parse to a tonnage).
// Rule types are picked from the real equipment-type list, so exact names
// are sufficient; list every variant on the rule ("Heat Pump", "Heat pump").
const typeMatches = (eqType, ruleTypes) => {
  if (!ruleTypes || ruleTypes.length === 0) return true;
  const t = (eqType || '').trim().toLowerCase();
  if (!t) return false;
  return ruleTypes.some((rt) => (rt || '').trim().toLowerCase() === t);
};

// Does a supply rule match one equipment item?
export const matchesSupplyRule = (eq, rule) => {
  if (rule.active === false) return false;
  if (!typeMatches(eq.type, rule.equipmentTypes)) return false;
  if (rule.modelRegex) {
    try {
      if (!new RegExp(rule.modelRegex, 'i').test(eq.model || '')) return false;
    } catch (e) {
      return false; // broken regex — rule never matches
    }
  }
  if (rule.tonnageMin != null || rule.tonnageMax != null) {
    const t = parseTonnage(eq.model);
    if (t == null) return false;
    if (rule.tonnageMin != null && t < rule.tonnageMin) return false;
    if (rule.tonnageMax != null && t > rule.tonnageMax) return false;
  }
  return true;
};

// Find a supplier object by (free-text) name.
export const findSupplierByName = (name, suppliers) => {
  const n = (name || '').trim().toLowerCase();
  if (!n) return null;
  return (
    (suppliers || []).find((s) => {
      const sn = (s.name || '').trim().toLowerCase();
      return sn && (sn === n || sn.includes(n) || n.includes(sn));
    }) || null
  );
};

// Compute the supplies the rules add for a list of equipment items.
// Returns [{ item: {type,make,model,qty,instruction}, supplier, ruleName, triggeredBy }]
// — one entry per supply model per supplier, quantities accumulated across
// matching equipment units.
export const computeAutoSupplies = ({ equipment, rules, suppliers, fallbackSupplier = null }) => {
  const acc = new Map(); // `${supplierId}|${model}` -> entry
  (equipment || []).forEach((eq) => {
    const units = Math.max(1, Number(eq.qty) || 1);
    (rules || []).forEach((rule) => {
      if (!matchesSupplyRule(eq, rule)) return;
      const supplier = findSupplierByName(rule.supplierName, suppliers) || fallbackSupplier;
      (rule.items || []).forEach((it) => {
        if (!it.model) return;
        const key = `${supplier?.id || 'none'}|${it.model.toUpperCase()}`;
        const addQty = Math.max(1, Number(it.qty) || 1) * units;
        if (acc.has(key)) {
          acc.get(key).item.qty += addQty;
        } else {
          acc.set(key, {
            item: {
              type: it.type || 'Supply',
              make: it.make || '',
              model: it.model,
              qty: addQty,
              instruction: '',
            },
            supplier,
            ruleName: rule.name,
            triggeredBy: eq.model,
          });
        }
      });
    });
  });
  return [...acc.values()];
};

// A job scheduled for today or tomorrow cannot wait for a normal delivery:
// the order must be prepared for pickup by our courier (Will Call / Prepull).
export const isWillCallDate = (date) => {
  if (!date) return false;
  const d = dayjs(typeof date === 'string' ? date.split('T')[0] : date);
  if (!d.isValid()) return false;
  return d.startOf('day').diff(dayjs().startOf('day'), 'day') <= 1;
};

// Build the order email body. Shared between the manual modal and auto-order
// so both produce identical emails (including the Will Call / Prepull block).
export const buildOrderEmailText = ({
  supplierName,
  items,
  supplies = [], // supply items rendered as a separate "Supplies" section
  publicId,
  deliveryDate, // dayjs | 'YYYY-MM-DD' | null — the installation date
  createdBy,
  supplierInstruction = '',
  autoNote = '',
}) => {
  const d = deliveryDate ? dayjs(typeof deliveryDate === 'string' ? deliveryDate.split('T')[0] : deliveryDate) : null;
  const valid = d && d.isValid();
  const deliveryDateFormatted = valid ? d.format('MM/DD/YYYY') : '';
  const requestedDeliveryDate = valid ? d.subtract(2, 'day').format('MM/DD/YYYY') : '';
  const willCall = valid && isWillCallDate(d);

  const itemLine = (item) => {
    const base = [item.type, item.make, item.model].filter((x) => x && x !== '-').join(' ');
    return item.qty > 1 ? `${base} — Qty: ${item.qty}` : base;
  };
  const equipmentList = (items || []).map(itemLine).join('\n');
  const suppliesBlock = (supplies || []).length
    ? `\nSupplies:\n${supplies.map(itemLine).join('\n')}\n`
    : '';

  const itemInstructions = (items || [])
    .filter((item) => item.instruction)
    .map((item) => `Additional: ${item.instruction}`)
    .join('\n');

  let specialInstructionsText = '';
  if (itemInstructions || supplierInstruction || autoNote) {
    specialInstructionsText = '\n';
    if (itemInstructions) specialInstructionsText += itemInstructions + '\n';
    if (supplierInstruction) specialInstructionsText += supplierInstruction + '\n';
    if (autoNote) specialInstructionsText += autoNote + '\n';
  }

  const techName = createdBy?.fullName || '';
  const techEmail = createdBy?.email || '';
  const techPhone = createdBy?.phoneNumber || '';

  let contactLine = '';
  if (techEmail && techPhone) {
    contactLine = `\nYou can contact me by email: ${techEmail} and cell phone: ${techPhone}`;
  } else if (techEmail) {
    contactLine = `\nYou can contact me by email: ${techEmail}`;
  } else if (techPhone) {
    contactLine = `\nYou can contact me by cell phone: ${techPhone}`;
  }

  // Scheduling block: normal delivery vs Will Call / Prepull for jobs that
  // start today or tomorrow.
  let scheduleBlock = '';
  if (deliveryDateFormatted) {
    if (willCall) {
      const dayWord = d.startOf('day').diff(dayjs().startOf('day'), 'day') === 0 ? 'TODAY' : 'TOMORROW';
      scheduleBlock =
        `Job is scheduled for: ${deliveryDateFormatted} (${dayWord})\n` +
        `*** WILL CALL / PREPULL ***\n` +
        `Please pull this order and have it ready for pickup by our courier as soon as possible. Do NOT schedule a delivery.\n`;
    } else {
      scheduleBlock = `Job is scheduled for: ${deliveryDateFormatted}\nPlease deliver by: ${requestedDeliveryDate} (2 days before installation)\n`;
    }
  }

  return `*** PLEASE DO NOT REPLY TO THIS EMAIL — this mailbox is not monitored. ***
*** For any questions regarding this order, please use the contact information below. ***

Dear ${supplierName}

Please process this order as follows:

${scheduleBlock}PO: ${publicId}

Equipment in this order:
${equipmentList}
${suppliesBlock}${specialInstructionsText}${contactLine}

Thank you${techName ? `\n${techName}` : ''}`;
};

// Convert the plain-text email body to simple HTML (same as the manual modal).
export const emailTextToHtml = (plainText) =>
  (plainText || '')
    .split('\n')
    .map((line) => (line.trim() ? `<p>${line}</p>` : '<br/>'))
    .join('');

// Prepare the auto-order plan: which items go to which supplier, which are
// already ordered, which cannot be resolved to a supplier automatically, and
// which supplies the BOM rules add (see the Auto-Supplies settings tab).
export const buildAutoOrderPlan = ({ proposal, orders, suppliers, catalogSuppliers, supplyRules = [] }) => {
  const equipment = extractProposalEquipment(proposal).filter((e) => e.model && e.model !== '-');
  const sentModels = collectSentModels(orders);

  const alreadyOrdered = [];
  const unresolved = [];
  const bySupplier = {}; // supplierId -> { supplier, items, supplies }
  const toOrder = []; // not-yet-ordered equipment (supplies apply to these)

  equipment.forEach((eq) => {
    if (sentModels.has(eq.model)) {
      alreadyOrdered.push(eq);
      return;
    }
    toOrder.push(eq);
    const supplier = resolveSupplierForModel(eq.model, catalogSuppliers, suppliers);
    if (!supplier) {
      unresolved.push(eq);
      return;
    }
    if (!bySupplier[supplier.id]) bySupplier[supplier.id] = { supplier, items: [], supplies: [] };
    bySupplier[supplier.id].items.push({ ...eq, supplierId: supplier.id, supplierName: supplier.name });
  });

  // Supplies from the BOM rules. The fallback supplier (rule has no supplier
  // of its own) is the first equipment group's supplier.
  // When no option is accepted, the systems are ALTERNATIVES of the same job:
  // the same model repeated across options must count once, otherwise the
  // supplies double (seen on multi-option contracts in Oct 2026).
  let suppliesBasis = toOrder;
  if (!(proposal?.optionAccepted && proposal?.selectedOptionId)) {
    const seenModels = new Set();
    suppliesBasis = toOrder.filter((eq) => {
      const key = (eq.model || '').toUpperCase();
      if (seenModels.has(key)) return false;
      seenModels.add(key);
      return true;
    });
  }
  const fallbackSupplier = Object.values(bySupplier)[0]?.supplier || null;
  const supplies = computeAutoSupplies({ equipment: suppliesBasis, rules: supplyRules, suppliers, fallbackSupplier });
  const skippedSupplies = [];
  supplies.forEach((s) => {
    if (!s.supplier) {
      skippedSupplies.push(s);
      return;
    }
    if (!bySupplier[s.supplier.id]) bySupplier[s.supplier.id] = { supplier: s.supplier, items: [], supplies: [] };
    bySupplier[s.supplier.id].supplies.push(s.item);
  });

  return {
    equipmentCount: equipment.length,
    alreadyOrdered,
    unresolved,
    skippedSupplies,
    supplierGroups: Object.values(bySupplier),
    nothingToOrder: equipment.length === 0,
    allCovered: equipment.length > 0 && alreadyOrdered.length === equipment.length,
  };
};
